import {
  Button,
  ButtonOld,
  Dialog,
  DialogContent,
  SuccessDialog,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import { CreateBenefitModal } from 'containers/benefits';
import { useMe } from 'state/Administrators';
import { DateTimeSelectModal } from 'components/modals';
import { useBenefit, useUpdateBenefit } from 'store/benefits';
import moment from 'moment';
import { usePredefinedToasts } from 'utils/Toast';
import { routerHistory } from 'utils/router-history';

interface Props extends ComponentProps<typeof SuccessDialog> {
  benefitId: string;
}

const BenefitWizardCompletionSuccessDialog = ({
  benefitId,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { canEditBenefits } = useMe();
  const predefinedToasts = usePredefinedToasts();

  const { data: benefit, isLoading } = useBenefit(benefitId);

  const { mutate: updateBenefit, isLoading: isUpdating } = useUpdateBenefit();

  return (
    <Dialog {...props}>
      <DialogContent>
        <Typography variant="h2" align="center" pt="25px">
          {t('benefits.benefitCreatedSuccessfully')}
        </Typography>
        <Typography variant="p1" align="center" mt="20px" mb="15px">
          {t('benefits.publishPrompt')}
        </Typography>
        {benefit ? (
          <ButtonOld
            fullWidth
            size="large"
            variant="contained"
            color="success"
            textColor="white"
            disabled={isLoading || isUpdating}
            onClick={async () => {
              props.onClose?.();
              const response = await openModal({
                component: DateTimeSelectModal,
                props: {
                  title: t('benefits.setPublishDate'),
                  datePickerProps: {
                    maxDateTime: benefit.deactivatedAt
                      ? moment(benefit.deactivatedAt)
                      : undefined,
                  },
                  initialValues:
                    benefit.deactivatedAt &&
                    moment().isAfter(benefit.deactivatedAt)
                      ? { date: benefit.deactivatedAt }
                      : undefined,
                },
              });

              if (response.action === 'CONFIRM') {
                const { id, ...data } = benefit;
                updateBenefit(
                  {
                    benefitId,
                    data: {
                      ...data,
                      isDraft: false,
                      publishedAt: response.date,
                    },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      routerHistory.push(ROUTES.benefits.list);
                    },
                  },
                );
              }
            }}
          >
            {t('buttons.publish')}
          </ButtonOld>
        ) : null}

        <Divider sx={{ mt: '20px', mb: '10px' }}>
          <Typography color="textSecondary">{t('common.or')}</Typography>
        </Divider>
        <Stack width="100%" gap="10px">
          <Button
            variant="secondary"
            label={t('benefits.createAnotherBenefit')}
            onClick={() => {
              props.onClose?.();
              openModal({
                component: CreateBenefitModal,
              });
            }}
            disabled={!canEditBenefits}
          />

          <ButtonOld onClick={() => routerHistory.push(ROUTES.benefits.list)}>
            {t('benefits.goToBenefitsList')}
          </ButtonOld>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default BenefitWizardCompletionSuccessDialog;
